import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { defaultOptions, icons, INbGridOptions, NbGridRowType } from '@app/common/advanced/models/nb-grid';
import { NbGridLazyLoadDataSource } from '@app/common/advanced/models/nb-grid-lazy-load-data-source';
import { GetContextMenuItemsParams, GridReadyEvent, SortChangedEvent } from 'ag-grid-community';
import { isArray } from 'lodash';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UtilsService } from '@app/core/services/utils.service';

@Component({
  selector: 'nb-grid',
  templateUrl: './nb-grid.component.html',
  styleUrls: ['./nb-grid.component.scss'],
})
export class NbGridComponent implements OnInit, OnDestroy {
  @Input()
  options: INbGridOptions;

  @ViewChild('ref')
  ref: ElementRef;

  gridIcons = icons;

  rowModelType: NbGridRowType = 'clientSide';

  maxConcurrentDatasourceRequest = 1;

  windowResizeListener: Subscription;

  gridResizeListener: Subscription;

  constructor() {}

  /**
   * define right click behaviour,
   * first select clicked row, if other rows have been selected, clear their select status
   * then open context menu
   * @param param
   */
  getContextMenuItems = (param: GetContextMenuItemsParams) => {
    param.node.setSelected(true, true);
    if (isArray(this.options.rowContextMenuItems)) {
      return this.options.rowContextMenuItems;
    }

    return this.options.rowContextMenuItems(param);
  };

  buildServerSideDataSource() {
    return new NbGridLazyLoadDataSource(this.options);
  }

  resize(event) {
    if (this.ref.nativeElement.offsetParent !== null) {
      event.api.sizeColumnsToFit();
    }
  }

  onGridReady(event: GridReadyEvent) {
    this.windowResizeListener = fromEvent(window, 'resize')
      .pipe(debounceTime(250))
      .subscribe(() => {
        this.resize(event);
      });

    this.gridResizeListener = UtilsService.resizeObservable(this.ref.nativeElement)
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe(() => {
        this.resize(event);
      });

    if (this.options.enableLazyLoading) {
      const source = this.buildServerSideDataSource();
      event.api.setServerSideDatasource(source);
    }
    if (this.options.onGridReady) {
      this.options.onGridReady(event);
    }
  }

  onGridSort(event: SortChangedEvent) {
    if (this.options.enableLazyLoading) {
      // reset server side data source when grid sorting
      const source = this.buildServerSideDataSource();
      event.api.setServerSideDatasource(source);
    }

    if (this.options.onSortChanged) {
      this.options.onSortChanged(event);
    }
  }

  ngOnInit() {
    // apply default options
    this.options = { ...defaultOptions, ...this.options };

    this.options.columnDefs.forEach(def => {
      def.suppressMenu = !def.enableColumnMenu;
      if (def.cellFormat) {
        def.valueFormatter = def.cellFormat;
        delete def.cellFormat;
      }

      if (def.cellTemplate) {
        def.cellRenderer = def.cellTemplate;
        delete def.cellTemplate;
      }

      if (this.options.enableRowCheckboxSelection) {
        def.checkboxSelection = (params: any) => {
          return params.columnApi.getAllDisplayedColumns()[0] === params.column;
        };
        // TODO: temporarilly comment out for not showing column header checkbox
        // def.headerCheckboxSelection = (params: any) => {
        //   return params.columnApi.getAllDisplayedColumns()[0] === params.column;
        // };
      }
    });

    if (this.options.enableLazyLoading) {
      this.rowModelType = 'serverSide';
    }
  }

  ngOnDestroy() {
    if (this.windowResizeListener) {
      this.windowResizeListener.unsubscribe();
    }

    if (this.gridResizeListener) {
      this.gridResizeListener.unsubscribe();
    }

    if (this?.options?.onGridDestroy) {
      this.options.onGridDestroy();
    }
  }
}
